// Utils

import url from 'url'
import moment from 'moment'
import { browserHistory } from 'react-router'
import isPlainObject from 'lodash.isplainobject'
import lodashDebounce from 'lodash.debounce'
import lodashThrottle from 'lodash.throttle'

import {
  apiBase,
  menu,
  projectTypes,
  brandColors,
  facultyFacesAPIBase,
  facultyFacesAPIToken,
  huskyTalesAPIBase
} from 'config'

import UiStore from 'stores/UiStore'

//
// Rendezvous
//

export { projectSubmodelFromJS, modelFromJS } from './models'
export { default as shortcodes } from './shortcodes'
export { default as twitterEntities } from './twitterEntities'
export const openPopout = require('./openPopout').default
export const closePopout = require('./closePopout').default

//
// Basic
//

export function noop () {}

export function always (a) {
  return () => a
}

export function groupBy (coll, f) {
  return coll.reduce((acc, v) => {
    const k = f(v)

    if (acc.hasOwnProperty(k)) {
      acc[k].push(v)
    } else {
      acc[k] = [v]
    }

    return acc
  }, {})
}

export function windowDimensions () {
  const w = window
  const d = document
  const e = d.documentElement
  const g = d.getElementsByTagName('body')[0]
  const width = w.innerWidth || e.clientWidth || g.clientWidth
  const height = w.innerHeight || e.clientHeight || g.clientHeight

  return { width, height }
}

export function windowScroll () {
  if (typeof window.pageYOffset !== 'undefined') {
    return { x: window.pageXOffset, y: window.pageYOffset }
  } else {
    const d = document
    const b = d.body
    const de = d.documentElement
    const f = d.clientHeight ? de : b
    const x = f.scrollLeft
    const y = f.scrollTop

    return { x, y }
  }
}

export function throttle (...args) {
  return lodashThrottle(...args)
}

export function debounce (...args) {
  return lodashDebounce(...args)
}

export function apiFetch (endpoint, query = {}, ...args) {
  return apiFetchGeneric(apiBase, endpoint, query, ...args)
}

export function apiFetchGeneric (apiBaseURL, endpoint, query = {}, ...args) {
  // TODO remove encodeURIComponent when cors.io is gone
  const target = url.format({
    pathname: endpoint,
    query
  })
  const uri = apiBaseURL + target

  const prom = window.fetch(uri, ...args)
  .then(function checkStatus (response) {
    if (response.status >= 200 && response.status < 300) {
      return response
    } else {
      var error = new Error(response.statusText)
      error.code = response.status
      error.response = response
      throw error
    }
  })
  .then(function parseJSON (response) {
    return response.json()
  })

  UiStore.trackRequest(endpoint, prom)

  return prom
}

export function filterEqualsProp (arr, prop, value) {
  return arr.filter(a => a[prop] === value)
}

export function classesList (...args) {
  return args.reduce((acc, arg) => {
    if (Array.isArray(arg)) {
      return [
        ...acc,
        ...classesList(...arg)
      ]
    } else if (isPlainObject(arg)) {
      return [
        ...acc,
        ...classesList(...Object.keys(arg).filter(k => arg[k]))
      ]
    } else if (typeof arg === 'string') {
      return [
        ...acc,
        arg
      ]
    } else {
      return acc
    }
  }, [])
}

export function filterRequestsByNamespace (requests, namespace) {
  return requests.filter(({ ns }) => getFirstURLComponent(ns) === namespace)
}

/**
 * Prepend a forward slash if it doesn't have one (and isn't empty)
 */
export function ensureAbsolute (url) {
  return (!url || url.startsWith('http') || url.startsWith('/')) ? url : `/${url}`
}

/*
 * Prepend http:// if not already prepended. For external links.
 */
export function prependHttp (url) {
  return (!url || url.startsWith('http')) ? url : `http://${url}`
}

/*
 * Make a space-separated name url-friendly (no slashes) and dash-separated
 */
export function urlEncodeName (name) {
  return encodeURIComponent(name.replace(/\s/g, '-').replace(/,/g, ''))
}

/**
 * If color is a bootstrap brand (succes, warning, info, etc)
 * then return its hex color code string. otherwise, return color as-is
 */
export function parseColor (color) {
  return brandColors[color] || color
}

/**
 * Convert hex color to rgba
 */
export function convertHex (hex, opacity = 100) {
  hex = hex.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`
}

export function dateFormatShort (date) {
  const m = moment(date)
  return m.format('M/D/Y')
}

/**
 * Format a date[time]. If the time is 00:00:00, then don't include it.
 */
export function smartDate (str, includeTime = false) {
  const m = moment(str)

  if (!m.isValid() || str === undefined) {
    return null
  }

  const includeTimeFinal =
    includeTime && (
      m.hour() !== 0 ||
      m.minute() !== 0 ||
      m.second() !== 0
    )

  return m.format(includeTimeFinal ? 'LLL' : 'LL')
}

// Get menu item by slug
export function getMenuItem (slug) {
  return menu.find(item => item.slug === slug)
}

// Make a struct (map) of type slugs => boolean
// Represents active project types in UiStore.projectFilters.type
export function makeProjectTypesStruct (types = []) {
  return projectTypes.reduce((acc, type) => ({
    ...acc,
    [type]: types.includes(type)
  }), {})
}

export function goToPageWithSubMaybe (slug, subSlug = null) {
  // Push new url -- triggers route transition and filter resets
  const params = typeof subSlug === 'string'
    ? `?${slug === 'explore' ? 'types' : 'subCategories'}=${subSlug}`
    : ''

  browserHistory.push('/' + slug + params)
}

// Given a location.pathname, returns just the first part
export function getFirstURLComponent (pathname) {
  // from: http://stackoverflow.com/questions/5735483/regex-to-get-first-word-after-slash-in-url
  return /^\/([^/]*).*$/.exec(pathname)[1]
}

export function getFacultyFaceURL (netID) {
  return `${facultyFacesAPIBase}/faculty/face/${netID}?token=${facultyFacesAPIToken}`
}

export function getHuskyTalesURL (netID) {
  return `${huskyTalesAPIBase}/storysearch/${netID}`
}



// WEBPACK FOOTER //
// ./resources/assets/js/utils/index.js